import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useField } from '@unform/core';

type TVTextFieldProps = TextFieldProps & {
  name: string;
  calcular?: (novoValor: number) => void;
};

export const VFieldMoneynumber2nota: React.FC<TVTextFieldProps> = ({ calcular, name, ...rest }) => {

  const { fieldName, registerField, defaultValue, error, clearError } = useField(name);

  const [value, setValue] = useState<string>(defaultValue || '');

  useEffect(() => {
    registerField({
      name: fieldName,

      getValue: () => formatToNumber(value),

      setValue: (_, newValue) => {
        if (newValue) {
          setValue(formatToMoney(String(newValue)));
        } else {
          setValue('');
        }
      },
    });
  }, [registerField, fieldName, value]);

  // ---- CONVERTE STRING PARA NUMBER ----
  const formatToNumber = (val: string): number => {
    if (!val) return 0;
    
    // Remove pontos de milhar e substitui vírgula decimal por ponto
    const clean = val.replace(/\./g, '').replace(',', '.');
    
    return Number(clean);
  };

  // ---- FORMATA STRING PARA VISUALIZAÇÃO ----
  const formatToMoney = (val: string): string => {
    // Se já veio como número (ex: "91.9" ou 91.9)
    if (typeof val === 'string' && !isNaN(Number(val))) {
      // Converte para número e formata
      const num = Number(val);
      return num.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    
    // Remove tudo que não for dígito ou vírgula/ponto
    let cleanValue = val.replace(/[^\d,.-]/g, '');
    
    // Substitui ponto por vírgula se existir
    cleanValue = cleanValue.replace('.', ',');
    
    // Remove pontos de milhar existentes
    cleanValue = cleanValue.replace(/\./g, '');
    
    // Garante que só tem uma vírgula
    const parts = cleanValue.split(',');
    if (parts.length > 2) {
      cleanValue = parts[0] + ',' + parts.slice(1).join('');
    }
    
    // Separa parte inteira e decimal
    let integerPart = parts[0];
    let decimalPart = parts.length > 1 ? parts[1].slice(0, 2) : '';
    
    // Remove zeros à esquerda da parte inteira, mas mantém pelo menos um dígito
    integerPart = integerPart.replace(/^0+/, '') || '0';
    
    // Adiciona pontos de milhar
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Garante 2 dígitos decimais
    if (decimalPart.length === 1) {
      decimalPart = decimalPart + '0';
    } else if (decimalPart.length === 0 && cleanValue.includes(',')) {
      decimalPart = '00';
    }
    
    if (decimalPart) {
      return `${integerPart},${decimalPart}`;
    }
    
    return integerPart;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Se o usuário está digitando, formata progressivamente
    if (newValue === '') {
      setValue('');
      calcular?.(0);
      rest.onChange?.(e);
      return;
    }
    
    // Remove tudo que não for número
    const numbers = newValue.replace(/\D/g, '');
    
    if (numbers.length === 0) {
      setValue('');
      calcular?.(0);
      rest.onChange?.(e);
      return;
    }
    
    // Converte para centavos (ex: 123 -> 1,23)
    const cents = parseInt(numbers, 10);
    
    // Aplica formatação brasileira
    const formatted = (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    setValue(formatted);
    const numberValue = cents / 100;
    calcular?.(numberValue);
    
    // Cria um evento simulado para o onChange original
    const simulatedEvent = {
      ...e,
      target: {
        ...e.target,
        value: formatted
      }
    };
    rest.onChange?.(simulatedEvent as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <TextField
      {...rest}
      error={!!error}
      helperText={error}
      value={value}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (error) clearError();
        rest.onKeyDown?.(e);
      }}
    />
  );
};